<template>
  <div v-if="error && !fields.length" class="bg-white rounded-lg shadow p-6" data-testid="student-profile-form-error">
    <Message severity="error" :closable="false">
      <div class="flex flex-wrap items-center justify-between gap-3 w-full">
        <span>We could not load the details your school needs.</span>
        <Button label="Try again" icon="pi pi-refresh" size="small" severity="secondary" @click="retry"
          data-testid="btn-retry-profile-form" />
      </div>
    </Message>
  </div>

  <div v-else-if="fields.length" id="student-profile-form" ref="cardRef" class="bg-white rounded-lg shadow p-6 scroll-mt-20"
    data-testid="student-profile-form">
    <div class="flex items-center justify-between gap-3 mb-1">
      <h3 class="text-lg font-semibold">Information your school needs</h3>
      <Button v-if="entries.length" label="Save" icon="pi pi-check" size="small" :loading="saving" :disabled="!changed"
        @click="onSave" data-testid="btn-save-profile-form" />
    </div>
    <p class="text-sm text-surface-500 mb-4" data-testid="profile-form-intro">
      <template v-if="entries.length">Your school asks you to fill these in yourself. Fields marked * are required.</template>
      <template v-else>Details your school holds about you.</template>
    </p>

    <Message v-if="reminder" severity="warn" size="small" :closable="false" class="mb-4" data-testid="profile-form-reminder">
      {{ reminder }}
    </Message>

    <form v-if="entries.length" @submit.prevent="onSave">
      <CustomFieldsFormSection v-model="model" :entries="entries" :errors="errors" :maxLengths="maxLengths" :labelTags="tags"
        idPrefix="profile" testId="student-profile-form-inputs" />
      <div class="flex justify-end mt-4">
        <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" :disabled="!changed"
          data-testid="btn-save-profile-form-bottom" />
      </div>
    </form>

    <div v-if="readOnly.length" :class="{ 'mt-6 pt-4 border-t border-surface-200': entries.length }"
      data-testid="profile-form-read-only">
      <p class="text-xs text-surface-500 mb-2">Your school manages these. Contact your school to change them.</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div v-for="row in readOnly" :key="row.key" :data-testid="`profile-form-value-${row.key}`">
          <span class="text-surface-500 block">{{ row.label }}</span>
          <span class="font-medium">{{ row.value }}</span>
          <span v-if="row.locked" class="mt-0.5 flex items-center gap-1 text-xs text-surface-500" :data-testid="`profile-form-locked-${row.key}`">
            <i class="pi pi-lock text-[10px]" /> Locked by your school
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import CustomFieldsFormSection from '@/components/common/CustomFieldsFormSection.vue';
import { useStudentProfileFormDraft, CHECK_FIELDS_MESSAGE } from '@/composables/forms/useStudentProfileFormDraft';

/**
 * The fields the student's school opened to them on their profile: the ones
 * they may fill in, with required markers and inline errors, and the ones
 * shown read-only. Hidden when the school opened none.
 */
const emit = defineEmits<{
  /** Saved values of built-in fields, so the signed-in student can show them. */
  (e: 'saved', values: Record<string, string | null>): void;
}>();

/** The hash the home-page reminder links to. */
const CARD_HASH = '#student-profile-form';

const toast = useToast();
const route = useRoute();
const {
  fields, entries, readOnly, reminder, maxLengths, tags, model, changed, errors, saving, error, refetch, save,
} = useStudentProfileFormDraft();

// Arriving from the reminder: bring the card into view once it has loaded
// (on a phone it sits below the photo and contact cards).
const cardRef = ref<HTMLElement | null>(null);
const stopScroll = watch(() => fields.value.length, async(count) => {
  if (!count || route?.hash !== CARD_HASH) return;
  await nextTick();
  cardRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  stopScroll();
}, { immediate: true });

/** The error is on screen already: a failed retry needs no second report. */
function retry() {
  refetch()?.catch(() => {});
}

async function onSave() {
  const result = await save();
  if (result.ok) {
    emit('saved', result.builtIn);
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Thank you. Your school can now see these details.', life: 3000 });
  } else {
    toast.add({
      severity: result.message === CHECK_FIELDS_MESSAGE ? 'warn' : 'error',
      summary: 'Could not save',
      detail: result.message,
      life: 5000,
    });
  }
}
</script>
